/**
 * theme.store — store Zustand do tema visual.
 *
 * Spec §8:
 *   • `theme` ∈ {'light', 'dark'} — sem `system` por enquanto.
 *   • `toggle()` alterna entre os dois valores, persiste em localStorage
 *     e aplica/remove a classe `dark` no `<html>` (estratégia darkMode:'class'
 *     do Tailwind, configurada em tailwind.config.ts).
 *
 * Estado inicial: lê preferência salva no localStorage; se ausente, usa o
 * `prefers-color-scheme` do SO; fallback final é `light`.
 *
 * A aplicação da classe no DOM acontece tanto no boot (`applyThemeToDom`)
 * quanto a cada `toggle`, para que o efeito visual seja imediato sem depender
 * de um `useEffect` em algum componente.
 */
import { create } from 'zustand'

import { STORAGE_KEYS } from '@/lib/constants'

export type Theme = 'light' | 'dark'

export interface ThemeState {
  theme: Theme
}

export interface ThemeActions {
  /** Alterna entre `light` e `dark`. */
  toggle: () => void
  /** Define um tema específico (útil para inicialização explícita ou testes). */
  setTheme: (theme: Theme) => void
}

export type ThemeStore = ThemeState & ThemeActions

function readPersistedTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEYS.THEME)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    return null
  }
}

function writePersistedTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEYS.THEME, theme)
  } catch {
    // localStorage indisponível — ignora silenciosamente.
  }
}

/** Aplica/remove a classe `dark` no `<html>` (estratégia darkMode:'class'). */
export function applyThemeToDom(theme: Theme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (theme === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

/** Determina o tema inicial: localStorage > prefers-color-scheme > light. */
function resolveInitialTheme(): Theme {
  const persisted = readPersistedTheme()
  if (persisted) return persisted
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    try {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    } catch {
      /* ignora */
    }
  }
  return 'light'
}

const initialTheme = resolveInitialTheme()
// Aplica imediatamente no DOM para evitar FOUC entre primeiro paint e primeiro toggle.
applyThemeToDom(initialTheme)

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: initialTheme,

  toggle: () => {
    const next: Theme = get().theme === 'light' ? 'dark' : 'light'
    writePersistedTheme(next)
    applyThemeToDom(next)
    set({ theme: next })
  },

  setTheme: (theme) => {
    writePersistedTheme(theme)
    applyThemeToDom(theme)
    set({ theme })
  },
}))
